// React
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
// Locales
import translate from 'locales/i18n';
// Material UI
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import ClearIcon from '@mui/icons-material/Clear';
import Button from '@mui/material/Button';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
// PCP Components
import DialogConfirm from 'components/dialogs/commons/DialogConfirm';
import DialogManageAccountInfo from 'components/dialogs/account/DialogManageAccount/DialogManageAccountInfo';
import DialogManageAccountModules from 'components/dialogs/account/DialogManageAccount/DialogManageAccountModules';
// Actions
import AccountAction from 'actions/AccountAction';
//Styles
import { useStyles } from 'components/dialogs/account/DialogAccount.style';

const DialogManageAccount = ({
    selectedAccount,
    close,
    open,
    sites
}) => {
    const [newAccount, setNewAccount] = useState({});
    const [accountSpeConfig, setAccountSpeConfig] = useState({});
    const [accountPeConfig, setAccountPeConfig] = useState({});
    const [accountTrendsConfig, setAccountTrendsConfig] = useState({});
    const [accountPerfConfig, setAccountPerfConfig] = useState({});
    const [accountSliderConfig, setAccountSliderConfig] = useState({});
    const [accountAdboxConfig, setAccountAdboxConfig] = useState({});
    const [accountDmConfig, setAccountDmConfig] = useState({});

    const [errorMessage, setErrorMessage] = useState('');
    const [activeStep, setActiveStep] = useState(0);

    const [dialogConfirmOpen, setDialogConfirmOpen] = useState(false);

    const steps = [translate.i18n('GENERAL_INFORMATIONS'), translate.i18n('MODULES_CONFIGURATION')];

    const classes = useStyles();

    useEffect(() => {
        if (open) {
            initAccount(selectedAccount);
        } else {
            initAccount({});
            setActiveStep(0);
        }
    },[open]);

    const initAccount = (account) => {
        setNewAccount({
            name: account?.name ? account.name : '',
            type: account?.type ? account.type : '',
            email: account?.email ? account.email : '',
            logoUrl: account?.logoUrl ? account.logoUrl : '',
            siteId: account?.site ? account.site.id : '',
            currency: account?.currency ? account.currency.code : '',
            shippingCountry: account?.shippingCountry ? account.shippingCountry.countryCode : '',
            taxMode: account?.taxMode ? account.taxMode : '',
            modules: account?.modules ? { ...account.modules } : {
                spe: false,
                perf: false,
                adbox: false,
                catalog: false,
                mail: false,
                slider: false,
                trends: false,
                mp: false,
                pe: false
            },
        });
        setAccountSpeConfig(account?.speConfig? { ...account.speConfig }:{
            sites: []
        });
        setAccountPeConfig(account?.peConfig? { ...account.peConfig }:{});
        setAccountTrendsConfig(account?.trendsConfig? { ...account.trendsConfig }:{});
        setAccountPerfConfig(account?.perfConfig? { ...account.perfConfig }:{
            sites: []
        });
        setAccountSliderConfig(account?.sliderConfig? { ...account.sliderConfig }:{
            sites: []
        });
        setAccountAdboxConfig(account?.adboxConfig? { ...account.adboxConfig }:{
            sites: [],
            credentials: {
                user: '',
                password: '',
                host: '',
                port: ''
            }
        });
        setAccountDmConfig(account?.dmConfig? { ...account.dmConfig }:{
            emailAddresses: [],
            sendingHour: 10
        });
    };

    const getAccountData = () => ({
        name: newAccount.name,
        type: newAccount.type,
        email: newAccount.email,
        logoUrl: newAccount.logoUrl,
        siteId: newAccount.siteId,
        currency: newAccount.currency,
        shippingCountry: newAccount.shippingCountry,
        taxMode: newAccount.taxMode,
        modules: newAccount.modules,
        speConfig: accountSpeConfig,
        peConfig: accountPeConfig,
        trendsConfig: accountTrendsConfig,
        perfConfig: accountPerfConfig,
        sliderConfig: accountSliderConfig,
        adboxConfig: accountAdboxConfig,
        dmConfig: accountDmConfig
    });

    const handleChange = (attribute, value) => {
        const accountCopy = { ...newAccount };
        accountCopy[attribute] = value;
        if (attribute === 'type' && value === 'SUPPLIER') accountCopy.siteId = '';
        setNewAccount(accountCopy);
    };

    const setAccountModules = (moduleName, value) => {
        const accountCopy = { ...newAccount };
        accountCopy.modules[moduleName] = value;
        setNewAccount(accountCopy);
    };

    const renderDialogConfirm = () =>
        <DialogConfirm
            open={dialogConfirmOpen}
            close={() => setDialogConfirmOpen(false)}
            title={translate.i18n('WARNING')}
            text={translate.i18n('CONFIRMATION_UPDATE_ACCOUNT', { accountName: newAccount.name })}
            callback={() => {
                AccountAction.updateAccount(getAccountData(), selectedAccount.id);
                close();
            }}
        />;

    const getStepContent = (stepIndex) => {
        switch (stepIndex) {
        case 0:
            return <DialogManageAccountInfo
                errorMessage={errorMessage}
                handleChange={handleChange}
                isEdit={!!selectedAccount}
                newAccount={newAccount}
                sites={sites}
            />;
        case 1:
            return <DialogManageAccountModules
                accountModules={newAccount.modules}
                setAccountModule={setAccountModules}
                setSpeConfig={setAccountSpeConfig}
                sites={sites}
                speConfig={accountSpeConfig}
                perfConfig={accountPerfConfig}
                setPerfConfig={setAccountPerfConfig}
                sliderConfig={accountSliderConfig}
                setSliderConfig={setAccountSliderConfig}
                adboxConfig={accountAdboxConfig}
                setAdboxConfig={setAccountAdboxConfig}
                dmConfig={accountDmConfig}
                setDmConfig={setAccountDmConfig}
            />;
        default:
            return 'Unknown stepIndex';
        }
    };

    const handleNext = () => {
        if (activeStep === 0) {
            if (checkInfo()) setActiveStep(1);
        } else {
            if (selectedAccount) {
                setDialogConfirmOpen(true);
            } else {
                AccountAction.addAccount(getAccountData());
                close();
            }
        }
    };

    const checkInfo = () => {
        if (newAccount.name === '') {
            setErrorMessage(translate.i18n('ERROR_MSG', { name: translate.i18n('NAME') }));
            return false;
        }

        if (newAccount.email === '') {
            setErrorMessage(translate.i18n('ERROR_MSG', { name: translate.i18n('EMAIL') }));
            return false;
        }

        if (newAccount.logoUrl === '') {
            setErrorMessage(translate.i18n('ERROR_MSG', { name: translate.i18n('LOGO_URL') }));
            return false;
        }

        if (newAccount.siteId === '' && newAccount.type === 'RETAILER') {
            setErrorMessage(translate.i18n('ERROR_RETAILER_SITE'));
            return false;
        }

        return true;
    };

    return <Dialog
        open={open}
        onClose={close}
        disableRestoreFocus={true}
        aria-labelledby='responsive-dialog-title'
        classes={{
            paper: classes.dialogPaper
        }}
    >
        <IconButton color='secondary' onClick={close} className={classes.closeButton}>
            <ClearIcon />
        </IconButton>
        <DialogTitle id='responsive-dialog-title' className={classes.dialogTitle}>{selectedAccount ? translate.i18n('EDIT_ACCOUNT') : translate.i18n('ADD_ACCOUNT')}</DialogTitle>
        <DialogContent className={classes.dialogContent}>
            <Stepper activeStep={activeStep} alternativeLabel>
                {steps.map((label, key) =>
                    <Step key={key}>
                        <StepLabel>{label}</StepLabel>
                    </Step>
                )}
            </Stepper>
            {renderDialogConfirm()}
            {getStepContent(activeStep)}
        </DialogContent>
        <DialogActions>
            <div className={classes.bottomButtonContainer}>
                <Button color='secondary'
                    onClick={close}>
                    <ClearIcon className={classes.leftIcon} />
                    <div>{translate.i18n('CANCEL')}</div>
                </Button>
                <div className={classes.bottomButtons}>
                    <Button
                        disabled={activeStep === 0}
                        onClick={() => setActiveStep(0)}
                        className={classes.backButton}
                    >
                        Back
                    </Button>
                    <Button variant='contained' color='primary' onClick={handleNext}>
                        {activeStep === steps.length - 1 ? selectedAccount ? translate.i18n('SAVE') : translate.i18n('ADD_ACCOUNT') : 'Next'}
                    </Button>
                </div>
            </div>
        </DialogActions>
    </Dialog>;
};

DialogManageAccount.propTypes = {
    selectedAccount: PropTypes.object,
    close: PropTypes.func,
    open: PropTypes.bool,
    sites: PropTypes.array
};

export default DialogManageAccount;
